import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type AdminMark, type Course } from '../../lib/api.ts';

export function AdminBaner() {
  const qc = useQueryClient();
  const courses = useQuery({ queryKey: ['courses'], queryFn: () => api.listCourses() });
  const [editId, setEditId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createCourse({ name: newName }),
    onSuccess: (c) => {
      setNewName('');
      setErr(null);
      qc.invalidateQueries({ queryKey: ['courses'] });
      setEditId(c.id);
    },
    onError: (e: ApiError) => setErr(e.message),
  });

  if (editId) {
    return <CourseEditor courseId={editId} onClose={() => { setEditId(null); qc.invalidateQueries({ queryKey: ['courses'] }); }} />;
  }

  return (
    <div>
      <h1 className="text-lg font-semibold mb-4">Baner</h1>
      <p className="text-xs text-slate-500 mb-4">
        En bane består av merker: en startlinje (to merker), rundingsmerker i rekkefølge, og en
        mållinje (kan være lik startlinja). Baner gjenbrukes og knyttes til en regatta under
        «Innstillinger» på regattaen.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (newName.trim()) create.mutate();
        }}
        className="flex gap-2 mb-4"
      >
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Navn på ny bane"
          className="flex-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
        />
        <button
          type="submit"
          disabled={create.isPending || !newName.trim()}
          className="bg-sky-600 hover:bg-sky-500 px-4 rounded text-sm font-semibold disabled:opacity-50"
        >
          + Ny bane
        </button>
      </form>
      {err && <div className="text-xs text-red-300 mb-3">{err}</div>}

      {courses.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : courses.data && courses.data.length > 0 ? (
        <ul className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
          {courses.data.map((c: Course) => (
            <li key={c.id} className="p-3 bg-slate-900/30 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{c.name}</div>
                {c.description && <div className="text-xs text-slate-400 truncate">{c.description}</div>}
              </div>
              <a href={`/bane/${c.id}`} target="rc-kart" className="text-xs text-sky-400 shrink-0">
                🗺️ Vis på kart
              </a>
              <button onClick={() => setEditId(c.id)} className="text-xs text-sky-400 shrink-0">
                Rediger
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center text-slate-500 text-sm p-6 border border-slate-800 rounded-xl">
          Ingen baner enda.
        </div>
      )}
    </div>
  );
}

function MarkSelect({
  marks,
  value,
  onChange,
  placeholder,
}: {
  marks: AdminMark[];
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
    >
      <option value="">— {placeholder} —</option>
      {marks.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name} {m.source === 'tracker' ? '📡' : '📍'}
        </option>
      ))}
    </select>
  );
}

function CourseEditor({ courseId, onClose }: { courseId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ['course', courseId], queryFn: () => api.getCourse(courseId) });
  const allMarks = useQuery({ queryKey: ['marks'], queryFn: () => api.listMarks() });

  if (detail.isLoading || allMarks.isLoading) return <p className="text-slate-500 text-sm">Laster…</p>;
  if (!detail.data) return <p className="text-red-400 text-sm">Fant ikke banen.</p>;

  return (
    <CourseEditorInner
      key={courseId}
      courseId={courseId}
      initial={detail.data}
      marks={(allMarks.data ?? []).filter((m) => m.active)}
      onClose={onClose}
      invalidate={() => {
        qc.invalidateQueries({ queryKey: ['course', courseId] });
        qc.invalidateQueries({ queryKey: ['courses'] });
      }}
    />
  );
}

function CourseEditorInner({
  courseId,
  initial,
  marks,
  onClose,
  invalidate,
}: {
  courseId: string;
  initial: { course: Course; start: string[]; finish: string[]; rounding: { markId: string; side: 'port' | 'starboard' | null }[] };
  marks: AdminMark[];
  onClose: () => void;
  invalidate: () => void;
}) {
  const [name, setName] = useState(initial.course.name);
  const [description, setDescription] = useState(initial.course.description ?? '');
  const [startA, setStartA] = useState(initial.start[0] ?? '');
  const [startB, setStartB] = useState(initial.start[1] ?? '');
  const sameInit =
    initial.finish.length === 2 &&
    initial.start.length === 2 &&
    initial.finish[0] === initial.start[0] &&
    initial.finish[1] === initial.start[1];
  const [sameAsStart, setSameAsStart] = useState(sameInit || initial.finish.length === 0);
  const [finishA, setFinishA] = useState(initial.finish[0] ?? '');
  const [finishB, setFinishB] = useState(initial.finish[1] ?? '');
  const [rounding, setRounding] = useState<{ markId: string; side: 'port' | 'starboard' | null }[]>(
    initial.rounding,
  );
  const [addMark, setAddMark] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      await api.updateCourse(courseId, { name, description: description || null });
      const start = [startA, startB].filter(Boolean);
      const finish = sameAsStart ? start : [finishA, finishB].filter(Boolean);
      await api.setCourseMarks(courseId, { start, finish, rounding });
      return true;
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: ApiError) => setErr(e.message),
  });

  const del = useMutation({
    mutationFn: () => api.deleteCourse(courseId),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const markName = (id: string) => marks.find((m) => m.id === id)?.name ?? '(ukjent/arkivert)';

  function moveRounding(i: number, dir: -1 | 1) {
    setRounding((cur) => {
      const next = [...cur];
      const j = i + dir;
      if (j < 0 || j >= next.length) return cur;
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onClose} className="text-xs text-slate-400">
          &larr; Tilbake til baner
        </button>
        <a href={`/bane/${courseId}`} target="rc-kart" className="text-xs text-sky-400 shrink-0">
          🗺️ Vis på kart
        </a>
      </div>
      <h1 className="text-lg font-semibold">Rediger bane</h1>
      <p className="text-xs text-slate-500 -mt-2">
        «Vis på kart» viser den lagrede banen — lagre først for å se endringer.
      </p>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">{err}</div>
      )}

      <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-3">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Navn</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Beskrivelse (valgfritt)</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </div>
      </div>

      <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-2">
        <h2 className="font-semibold text-sm text-emerald-300">Startlinje (to merker)</h2>
        <MarkSelect marks={marks} value={startA} onChange={setStartA} placeholder="Startmerke A (f.eks. komitébåt)" />
        <MarkSelect marks={marks} value={startB} onChange={setStartB} placeholder="Startmerke B (f.eks. pinne)" />
      </div>

      <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-sm text-red-300">Mållinje (to merker)</h2>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={sameAsStart} onChange={(e) => setSameAsStart(e.target.checked)} className="accent-sky-500" />
            Samme som startlinja
          </label>
        </div>
        {!sameAsStart && (
          <>
            <MarkSelect marks={marks} value={finishA} onChange={setFinishA} placeholder="Målmerke A" />
            <MarkSelect marks={marks} value={finishB} onChange={setFinishB} placeholder="Målmerke B" />
          </>
        )}
      </div>

      <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-2">
        <h2 className="font-semibold text-sm text-amber-300">Rundingsmerker (i rekkefølge)</h2>
        {rounding.length === 0 && <p className="text-xs text-slate-500">Ingen rundingsmerker.</p>}
        <ol className="space-y-2">
          {rounding.map((r, i) => (
            <li key={`${r.markId}-${i}`} className="flex items-center gap-2 text-sm">
              <span className="w-5 text-slate-500 tabular-nums">{i + 1}.</span>
              <span className="flex-1 min-w-0 truncate">{markName(r.markId)}</span>
              <select
                value={r.side ?? ''}
                onChange={(e) =>
                  setRounding((cur) =>
                    cur.map((x, j) => (j === i ? { ...x, side: (e.target.value || null) as 'port' | 'starboard' | null } : x)),
                  )
                }
                className="px-2 py-1 bg-slate-950 border border-slate-700 rounded text-xs"
              >
                <option value="">side?</option>
                <option value="port">babord</option>
                <option value="starboard">styrbord</option>
              </select>
              <button onClick={() => moveRounding(i, -1)} className="text-slate-400 px-1" title="Opp">▲</button>
              <button onClick={() => moveRounding(i, 1)} className="text-slate-400 px-1" title="Ned">▼</button>
              <button
                onClick={() => setRounding((cur) => cur.filter((_, j) => j !== i))}
                className="text-red-400 px-1"
                title="Fjern"
              >
                ✕
              </button>
            </li>
          ))}
        </ol>
        <div className="flex gap-2 pt-1">
          <MarkSelect marks={marks} value={addMark} onChange={setAddMark} placeholder="Legg til rundingsmerke" />
          <button
            type="button"
            onClick={() => {
              if (addMark) {
                setRounding((cur) => [...cur, { markId: addMark, side: 'port' }]);
                setAddMark('');
              }
            }}
            className="px-3 bg-slate-800 hover:bg-slate-700 rounded text-sm shrink-0"
          >
            Legg til
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="flex-1 bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
        >
          {save.isPending ? 'Lagrer…' : 'Lagre bane'}
        </button>
        <button onClick={onClose} className="px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded font-semibold">
          Avbryt
        </button>
      </div>
      <button
        onClick={() => {
          if (confirm(`Arkivere banen «${name}»?`)) del.mutate();
        }}
        className="w-full text-xs text-red-400 py-2"
      >
        Arkiver bane
      </button>
    </div>
  );
}
